/**
 * Core mortgage calculation utilities for live payment estimator,
 * buying power analysis, and DTI calculations.
 *
 * All functions are pure — no side effects, easily testable.
 */
import { numericToLegacyRateBand } from './creditScoreInput.js';

// ---------------------------------------------------------------------------
// Average market rates by credit tier (illustrative — replace with live feed)
// ---------------------------------------------------------------------------
const BASE_RATES_30 = {
  '760+': 6.25,
  '700-759': 6.50,
  '680-699': 6.75,
  '660-679': 7.00,
  '640-659': 7.25,
  '620-639': 7.50,
  '580-619': 7.75,
  '<580': 8.25,
};

const BASE_RATES_15 = {
  '760+': 5.50,
  '700-759': 5.75,
  '680-699': 6.00,
  '660-679': 6.25,
  '640-659': 6.50,
  '620-639': 6.75,
  '580-619': 7.00,
  '<580': 7.50,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Format an interest rate for display. All posted rates use 3 decimal places.
 * Returns e.g. "6.400%". Pass includePercent=false to omit the % sign.
 */
export function formatRate(rate, includePercent = true) {
  const num = Number(rate);
  if (!Number.isFinite(num)) return includePercent ? `${rate}%` : `${rate}`;
  return `${num.toFixed(3)}${includePercent ? '%' : ''}`;
}

/**
 * Monthly P&I payment using standard amortization formula.
 */
export function monthlyPI(principal, annualRate, termYears) {
  if (!principal || principal <= 0 || !annualRate || annualRate <= 0 || !termYears) return 0;
  const r = annualRate / 100 / 12;
  const n = termYears * 12;
  return principal * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}

/**
 * Estimate PMI monthly cost.
 * Rough: 0.5-1.5% of loan amount / 12, scaled by LTV.
 */
export function estimatePMI(loanAmount, homeValue) {
  if (!loanAmount || !homeValue || homeValue <= 0) return 0;
  const ltv = loanAmount / homeValue;
  if (ltv <= 0.80) return 0; // No PMI at 80% LTV or below
  // Scale: 0.5% at 81% LTV → 1.2% at 97% LTV
  const pmiRate = 0.005 + (ltv - 0.80) * 0.04;
  return (loanAmount * pmiRate) / 12;
}

/**
 * Estimate monthly property tax.
 */
export function monthlyPropertyTax(homeValue, annualTaxRate = 1.1) {
  if (!homeValue) return 0;
  return (homeValue * (annualTaxRate / 100)) / 12;
}

/**
 * Estimate monthly homeowners insurance.
 */
export function monthlyInsurance(homeValue) {
  if (!homeValue) return 0;
  // ~$1,200/yr per $300k of value → roughly 0.4% of value
  return (homeValue * 0.004) / 12;
}

// ---------------------------------------------------------------------------
// Estimated rate lookup
// ---------------------------------------------------------------------------

export function getEstimatedRate(creditScore, termYears = 30) {
  const table = termYears <= 15 ? BASE_RATES_15 : BASE_RATES_30;
  const key = table[creditScore] ? creditScore : numericToLegacyRateBand(creditScore);
  return table[key] || table['700-759'];
}

// ---------------------------------------------------------------------------
// Live Payment Estimator
// ---------------------------------------------------------------------------

/**
 * Given partial borrower answers, compute a live payment estimate.
 * Returns null if insufficient data, otherwise a breakdown object.
 */
export function calculateLivePayment(answers) {
  const homeValue = parseFloat(answers.estimatedValue) || 0;
  const downPayment = parseFloat(answers.downPayment) || 0;
  const creditScore = answers.creditScore || '';

  if (!homeValue) return null;

  const loanAmount =
    answers.purchaseOrRefinance === 'refinance'
      ? downPayment
      : Math.max(0, homeValue - downPayment);
  if (loanAmount <= 0) return null;

  const rate = getEstimatedRate(creditScore, 30);
  const pi = monthlyPI(loanAmount, rate, 30);
  const pmi = estimatePMI(loanAmount, homeValue);
  const tax = monthlyPropertyTax(homeValue);
  const insurance = monthlyInsurance(homeValue);
  const total = pi + pmi + tax + insurance;

  const ltv = homeValue > 0 ? (loanAmount / homeValue) * 100 : 0;

  return {
    loanAmount: Math.round(loanAmount),
    rate,
    principalAndInterest: Math.round(pi),
    pmi: Math.round(pmi),
    propertyTax: Math.round(tax),
    insurance: Math.round(insurance),
    totalMonthly: Math.round(total),
    ltv: Math.round(ltv * 10) / 10,
    totalInterest: Math.round(pi * 360 - loanAmount),
  };
}

// ---------------------------------------------------------------------------
// Buying Power Analysis
// ---------------------------------------------------------------------------

/**
 * Calculate max affordable home price and related metrics.
 * Uses the 28/36 rule (housing ≤ 28% gross, total debt ≤ 36%).
 */
export function calculateBuyingPower(answers) {
  const annualIncome = parseFloat(answers.annualIncome) || 0;
  const monthlyDebts = parseFloat(answers.monthlyDebts) || 0;
  const downPayment = parseFloat(answers.downPayment) || 0;
  const creditScore = answers.creditScore || '';

  if (!annualIncome) return null;

  const monthlyGross = annualIncome / 12;
  const rate = getEstimatedRate(creditScore, 30);

  // Front-end ratio: max 28% of gross for housing
  const maxHousingPayment = monthlyGross * 0.28;

  // Back-end ratio: max 43% of gross for all debt (more realistic for modern lending)
  const maxTotalDebt = monthlyGross * 0.43;
  const maxHousingFromBackEnd = maxTotalDebt - monthlyDebts;

  // Use the more conservative of the two
  const maxPayment = Math.max(0, Math.min(maxHousingPayment, maxHousingFromBackEnd));

  // Subtract estimated tax + insurance (assume 1.5% of home value / 12)
  // We need to solve iteratively since tax/insurance depend on home value
  // Simplified: assume tax+insurance ≈ 18% of total payment
  const piPayment = maxPayment * 0.82; // ~82% goes to P&I

  // Reverse-engineer max loan from P&I payment
  const r = rate / 100 / 12;
  const n = 360;
  const maxLoan = piPayment > 0
    ? piPayment * (Math.pow(1 + r, n) - 1) / (r * Math.pow(1 + r, n))
    : 0;

  const maxHomePrice = Math.round(maxLoan + downPayment);

  // Comfortable payment (using actual max home price)
  const comfortableLoan = maxHomePrice - downPayment;
  const comfortablePI = monthlyPI(comfortableLoan, rate, 30);
  const comfortableTax = monthlyPropertyTax(maxHomePrice);
  const comfortableInsurance = monthlyInsurance(maxHomePrice);
  const comfortablePMI = estimatePMI(comfortableLoan, maxHomePrice);
  const comfortableTotal = comfortablePI + comfortableTax + comfortableInsurance + comfortablePMI;

  // DTI calculation
  const currentDTI = monthlyGross > 0
    ? ((monthlyDebts) / monthlyGross) * 100
    : 0;
  const projectedDTI = monthlyGross > 0
    ? ((monthlyDebts + comfortableTotal) / monthlyGross) * 100
    : 0;

  // DTI health
  let dtiHealth = 'excellent';
  if (projectedDTI > 43) dtiHealth = 'high';
  else if (projectedDTI > 36) dtiHealth = 'moderate';
  else if (projectedDTI > 28) dtiHealth = 'good';

  return {
    maxHomePrice: Math.round(maxHomePrice),
    maxLoanAmount: Math.round(maxLoan),
    comfortablePayment: Math.round(comfortableTotal),
    estimatedRate: rate,
    currentDTI: Math.round(currentDTI * 10) / 10,
    projectedDTI: Math.round(projectedDTI * 10) / 10,
    dtiHealth,
    monthlyGross: Math.round(monthlyGross),
    downPayment: Math.round(downPayment),
    breakdown: {
      principalAndInterest: Math.round(comfortablePI),
      propertyTax: Math.round(comfortableTax),
      insurance: Math.round(comfortableInsurance),
      pmi: Math.round(comfortablePMI),
    },
  };
}
